import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import { IonButton, IonButtons, IonItem, IonLabel } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { Card } from '../components/Card';
import { Text } from '../components/Text';
import { CareMapState } from '../caremap/CareMapState';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { QueryParamDrivenDrawerHelper } from '../components/query-param-driver-drawer/QueryParamDrivenDrawerHelper';
import { CaremapInsightsModelFactory } from './CaremapInsightsModelFactory';
import { InsightsModel } from './InsightsModel';
import { IInsight } from './IInsight';
import { useInsightEnterAnimation } from './useInsightEnterAnimation';
import { InsightSummaryContent } from './InsightSummaryContent';

// How many recent insights to tease on home. The full list (incl. low-confidence) lives on the page.
const HOME_COUNT = 2;

const useStyles = createUseStyles({
    // The home cards sit 20px apart and ion-card only contributes 10px of that. flow-root keeps the
    // card's own margins inside this wrapper, so the extra 10px exists only when the section renders.
    section: {
        display: 'flow-root',
        marginBottom: 10,
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        // The View All button is 44px tall and centres against the avatar and title, so it, not the
        // padding, sets where the header ink lands: 10px here puts the avatar 16px below the card's
        // top edge, matching the other home cards. The rows below carry their own leading, so the
        // header needs no bottom margin for the title-to-first-row gap to match them.
        paddingTop: 10,
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        paddingLeft: 16,
    },
    avatar: {
        width: 32,
        height: 32,
        borderRadius: '50%',
        objectFit: 'cover' as const,
    },
});

const InsightHomeRow: FC<{ insight: IInsight; isLast: boolean; onOpen: (insight: IInsight) => void }> = ({ insight, isLast, onOpen }) => {
    return (
        <IonItem
            button
            detail
            lines={isLast ? 'none' : undefined}
            onClick={() => onOpen(insight)}
            data-type="insight-home-card"
            data-insight-id={insight.insightId}
            style={{'--border-radius': '8px'}}
        >
            <IonLabel className="ion-text-wrap">
                <InsightSummaryContent insight={insight} />
            </IonLabel>
        </IonItem>
    );
};

// The home insights teaser: a section shaped like Recent Summaries (header + View All + a couple of
// recents), each card fully clickable into its detail drawer. Replaces the old one-line strip. Shows
// only medium/high insights (getActiveProactive) -- low-confidence is pull-only, on the page.
export const InsightsHomeSection: FC = () => {
    const classes = useStyles();

    const factory = useInstance(CaremapInsightsModelFactory);
    useSubscription(() => factory);

    const careMapState = useInstance(CareMapState);
    const history = useInstance(HistoryViewModel);
    const drawerHelper = useInstance(QueryParamDrivenDrawerHelper);

    let model: InsightsModel | undefined;
    try {
        model = factory.forActiveCaremap();
    } catch {
        // no active caremap yet -- render nothing rather than crash the home page
    }

    const insights = model ? model.getActiveProactive().slice(0, HOME_COUNT) : [];
    const enterClassName = useInsightEnterAnimation(insights.map(insight => insight.insightId));

    if (!model || insights.length === 0) {
        return null;
    }

    const onViewAll = () => {
        const careMapId = careMapState.getActiveCaremap()?.careMapId;
        if (careMapId) {
            history.push(`/insights/${careMapId}`);
        }
    };

    const onOpen = (insight: IInsight) => {
        drawerHelper.open({ mode: 'view-insight', insightId: insight.insightId, surface: 'home' });
    };

    return (
        <div className={classes.section}>
            <Card data-type="insights-home-section">
                <div className={classes.header} data-id="insights-section">
                    <div className={classes.headerLeft}>
                        <img src="/assets/images/help/helper-bea.png" alt="Bea" className={classes.avatar} />
                        <Text><b>Bea Noticed</b></Text>
                    </div>
                    <IonButtons slot="end">
                        <div style={{ marginRight: 12 }}>
                            <IonButton fill="clear" color="primary" data-id="view-all-insights" onClick={onViewAll}>
                                View All
                            </IonButton>
                        </div>
                    </IonButtons>
                </div>

                {insights.map((insight, idx) => (
                    <div key={insight.insightId} className={enterClassName(insight.insightId)}>
                        <InsightHomeRow insight={insight} isLast={idx === insights.length - 1} onOpen={onOpen} />
                    </div>
                ))}
            </Card>
        </div>
    );
};
